import { motion } from 'framer-motion';

export function WhyChooseUs() {
  const stats = [
    { value: '40+', label: 'Years Experience' },
    { value: '5,000+', label: 'Projects Completed' },
    { value: '5★', label: 'Google Rating' },
    { value: '900+', label: 'Locations Nationwide' },
  ];

  const features = [
    {
      icon: 'ri-award-line',
      title: 'Expert Craftsmanship',
      description:
        '40+ years of Signarama expertise means every project benefits from proven systems, skilled designers, and certified installers who take pride in flawless execution.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Premium Materials Only',
      description:
        'We use 3M and Avery Dennison vinyl, LED systems, and industrial-grade substrates — materials engineered for Texas heat, weather, and years of lasting performance.',
    },
    {
      icon: 'ri-timer-flash-line',
      title: 'Fast Turnaround',
      description:
        'Most orders are completed in 3–7 business days. Same-week rush production is available for qualifying projects without sacrificing quality or precision.',
    },
    {
      icon: 'ri-palette-line',
      title: 'In-House Design Team',
      description:
        'Our designers work directly with your brand to deliver concepts that are sharp, strategic, and built to convert attention into customers for your business.',
    },
    {
      icon: 'ri-tools-line',
      title: 'End-to-End Service',
      description:
        'From first consultation through final installation, we manage every step. One team, one point of contact, and full accountability for your project timeline.',
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Local DFW Experts',
      description:
        'We know Arlington, we know DFW, and we understand local zoning, permitting, and visibility requirements that affect signage performance in our market.',
    },
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20 bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-8"
        >
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-orange-400 mb-1">
                {stat.value}
              </div>
              <div className="text-gray-400 text-sm">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-teal-600 text-sm font-semibold tracking-widest uppercase mb-3 block">
            Why Arlington Businesses Choose Us
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Why Choose{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-orange-500">
              Signarama Arlington
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            We combine global franchise expertise with local knowledge to deliver signage
            that works harder for your business — from design through installation.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-100 hover:border-teal-200 hover:shadow-lg transition-all"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6">
                <i className={`${feature.icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">{feature.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Mid-page CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-6 bg-gradient-to-r from-teal-600 to-teal-700 rounded-3xl p-8"
        >
          <div>
            <h3 className="text-2xl font-bold text-white mb-1">
              Ready to get started?
            </h3>
            <p className="text-teal-100 text-sm">
              Free consultation · Fast quote · Expert advice for Arlington, TX businesses
            </p>
          </div>
          <div className="flex items-center space-x-4 flex-shrink-0">
            <a
              href="tel:817-860-9310"
              className="flex items-center space-x-2 text-white font-bold hover:text-teal-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-lg"></i>
              <span>817-860-9310</span>
            </a>
            <button
              onClick={() => {
                const el = document.getElementById('get-quote') || document.getElementById('quote-form');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-7 py-3 bg-white text-teal-700 font-bold rounded-full hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer"
            >
              Get a Free Quote
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
